/* MUI */
import {
    Button,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup
} from '@mui/material';
import { TagsPicker } from 'application/Shared/Components/Pickers';
import { Notify } from 'application/Shared/Helpers';
// Modals are imported directly here due to a circular dependency.
import { Modal } from 'application/Shared/Modals/Components/Modal';
import { ModalBody } from 'application/Shared/Modals/Components/ModalBody';
import { ModalFooter } from 'application/Shared/Modals/Components/ModalFooter';

/*  Shared  */
import { debug } from 'application/Shared/Utils';
import {
    usePostTagLink,
    usePutTagLink
} from 'hooks/dataAccess/GlobalTag/tagLink';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Components */
import { AddNewTagModalBody } from './AddNewTag';

export const updateTagLinks = async ({
    applicationId,
    currentTagLinks,
    onClose,
    platformTenantId,
    selectedTagIds,
    setSaving,
    postTagLink,
    putTagLink,
    t
}) => {
    const currentAppliedTags = currentTagLinks.filter((tl) => tl.applyTag);
    const currentHiddenTags = currentTagLinks.filter((tl) => !tl.applyTag);

    // If no current tags AND none selected OR no changes in selected tags
    // - do nothing / notify user of settings saved and close modal
    if (
        (currentTagLinks.length === 0 &&
            (selectedTagIds.length === 0 || selectedTagIds.join('') === '0')) ||
        (currentAppliedTags.length === selectedTagIds.length &&
            currentAppliedTags.every((tagLink) =>
                selectedTagIds.includes(tagLink.globalTagId)
            ))
    ) {
        Notify.s(t('EditTagsModal.updateTagLink.success'));
        onClose();
        return;
    }

    setSaving(true);

    // For all selected tagIds to add
    // - filter out any selected tagIds that already have a tagLink created.
    const tagsIdsToLink = selectedTagIds.filter(
        (tagId) =>
            tagId !== '0' &&
            !currentAppliedTags.find((tagLink) => tagLink.globalTagId === tagId)
    );

    // Check that all currentAppliedTags are part of selectedTagIds
    // If not, "remove" the tag link (set to `applyTag: false`)
    const tagLinksToRemove = currentAppliedTags.filter(
        (tagLink) => !selectedTagIds.includes(tagLink.globalTagId)
    );

    let failureOccurred = false;

    for (let i = 0; i < tagsIdsToLink.length; i += 1) {
        const tagId = tagsIdsToLink[i];

        // check if the viewed tenant has a hidden tagLink.
        const hiddenTagLink = currentHiddenTags.find(
            (tl) =>
                tl.platformTenantId === platformTenantId &&
                tl.globalTagId === tagId
        );

        // If the tagLink exists, but is not applied, then we need to update the existing tagLink.
        if (hiddenTagLink) {
            // eslint-disable-next-line no-await-in-loop
            const success = await putTagLink({
                applyTag: true,
                platformTenantId,
                shouldNotify: false,
                tagLinkId: hiddenTagLink.id
            });

            if (!success) {
                debug.e('Error applying tag link');
                failureOccurred = true;
            }
        } else {
            // Otherwise, create a new tagLink.
            // eslint-disable-next-line no-await-in-loop
            const success = await postTagLink({
                applicationId,
                applyTag: true,
                globalTagId: tagId,
                platformTenantId,
                shouldNotify: false
            });
            if (!success) {
                debug.e('Error creating tag link');
                failureOccurred = true;
            }
        }
    }

    for (let i = 0; i < tagLinksToRemove.length; i += 1) {
        const tagLink = tagLinksToRemove[i];
        // eslint-disable-next-line no-continue
        if (!tagLink) continue;

        // If the tagLink platformTenantId is matches the viewed tenant's platformTenantId,
        // that means we can just update the tagLink since it already belongs to the viewed tenant.
        if (tagLink.platformTenantId === platformTenantId) {
            // eslint-disable-next-line no-await-in-loop
            const success = await putTagLink({
                applyTag: false,
                platformTenantId,
                shouldNotify: false,
                tagLinkId: tagLink.id
            });
            if (!success) {
                debug.e('Error removing tag link');
                failureOccurred = true;
            }
        } else {
            // If the tagLink's platformTenantId is different than the currently viewed tenant,
            // that means the partner has a tagLink of `applyTag: true`, but the client has chosen
            // to override it, which we handle by adding a NEW tagLink that belongs to the client
            // with `applyTag` set to `false`.

            // eslint-disable-next-line no-await-in-loop
            const success = await postTagLink({
                applicationId,
                applyTag: false,
                globalTagId: tagLink.globalTagId,
                platformTenantId,
                shouldNotify: false
            });
            if (!success) {
                debug.e('Error creating tag link');
                failureOccurred = true;
            }
        }
    }

    if (failureOccurred) {
        Notify.i(t('EditTagsModal.updateTagLink.failure'));
    } else {
        Notify.s(t('EditTagsModal.updateTagLink.success'));
    }
    setSaving(false);
    onClose();
};

const EditTagLinks = ({
    applicationId,
    currentTagLinks,
    onClose,
    postTagLink,
    putTagLink
}) => {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const { partnerId, clientId } = useParams();

    const platformTenantId = clientId || partnerId;
    const currentTagIds = currentTagLinks
        .filter((tl) => tl.applyTag)
        .map((tl) => tl.globalTagId);

    const [selectedTagIds, setSelectedTagIds] = useState(currentTagIds);

    return (
        <>
            <ModalBody style={{ marginTop: '16px' }}>
                <TagsPicker
                    applicationId={applicationId}
                    multiple
                    disableAllOption
                    value={selectedTagIds.length > 0 ? selectedTagIds : ['0']}
                    onChange={(v) => setSelectedTagIds(v)}
                    unselectedOption={t('Tags.unselected')}
                    unselectedValue={['0']}
                />
            </ModalBody>
            <ModalFooter>
                <Button onClick={onClose} color="secondary" variant="contained">
                    {t('Common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    disabled={saving}
                    onClick={() =>
                        updateTagLinks({
                            applicationId,
                            currentTagLinks,
                            onClose,
                            platformTenantId,
                            postTagLink,
                            putTagLink,
                            selectedTagIds,
                            setSaving,
                            t
                        })
                    }
                    color="primary"
                >
                    {t('Common.save')}
                </Button>
            </ModalFooter>
        </>
    );
};

export const EditTagLinksModal = ({
    applicationId,
    currentTagLinks,
    onClose,
    open
}) => {
    const { t } = useTranslation();

    const { partnerId, clientId } = useParams();
    const platformTenantId = clientId || partnerId;

    const [editingMode, setEditingMode] = useState(true);

    const { postTagLink } = usePostTagLink();
    const { putTagLink } = usePutTagLink();

    const onCreate = async ({ color, description, name }) => {
        await postTagLink({
            applicationId,
            applyTag: true,
            color,
            description,
            name,
            onClose,
            platformTenantId,
            t
        });
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t('EditTagsModal.title')}
            paperProps={{
                minWidth: ['400px', '720px'],
                width: '600px'
            }}
            disableEasyClose
        >
            <>
                <Grid display="flex" marginY="16px" marginX="24px">
                    <RadioGroup
                        row
                        aria-labelledby="edit-tags-radiogroup"
                        name="edit-tags-radio-buttons-group"
                        value={editingMode ? 'editing' : 'creating'}
                        onChange={(e) => {
                            setEditingMode(e.target.value === 'editing');
                        }}
                    >
                        <FormControlLabel
                            value="editing"
                            control={<Radio />}
                            label={t('Application.use_existing_tags')}
                        />
                        <FormControlLabel
                            value="creating"
                            control={<Radio />}
                            label={t('Application.create_new_tag')}
                        />
                    </RadioGroup>
                </Grid>
                {editingMode && (
                    <EditTagLinks
                        applicationId={applicationId}
                        onClose={onClose}
                        currentTagLinks={currentTagLinks}
                        postTagLink={postTagLink}
                        putTagLink={putTagLink}
                    />
                )}
                {!editingMode && (
                    <AddNewTagModalBody onClose={onClose} onCreate={onCreate} />
                )}
            </>
        </Modal>
    );
};
